/**
 * PURPOSE: Lienzo de firma con Pointer Events (mouse, dedo y lápiz) para el pad de la Vista Planilla FEDUP (FTR-0062).
 * RESPONSIBILITIES:
 *  - Sí: capturar el puntero (setPointerCapture) desde que se aprieta hasta que se suelta, aunque salga del lienzo; `touch-action: none`
 *    para que el dedo no desplace la página; puntos intermedios con getCoalescedEvents cuando el navegador los da.
 *  - Sí: mapear CADA evento con el getBoundingClientRect del momento (vale con devicePixelRatio, scroll de la página o del contenedor,
 *    zoom del navegador y transform/posición fija de los ancestros) → la tinta cae bajo el cursor.
 *  - Sí: resolución interna = tamaño CSS × devicePixelRatio, re-sincronizada con ResizeObserver, cambio de DPR y al empezar cada trazo;
 *    los trazos (normalizados) se redibujan sin deformarse.
 *  - Sí: API imperativa: clear, isEmpty, getStrokes, toSignaturePng (PNG final para la casilla) y getCanvas.
 *  - No: no guarda nada ni conoce partidos; no usa react-signature-canvas (la firma de básquet sigue con ese componente sin cambios).
 * COLLABORATORS:
 *  - signatureStrokes.js, FedupSignaturePad.jsx
 * ROUTE: src/components/FullScreenModal/FullScreenPlanilla/shared/fedupSignatures/SignatureCanvas.jsx
 */

import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useRef } from "react";

import { PAD_LINE_WIDTH_CSS, drawStrokes, exportSignaturePng, pushPoint, strokesAreEmpty, toNormalizedPoint } from "./signatureStrokes.js";

const createDomCanvas = (w, h) => {
  const c = document.createElement("canvas");
  c.width = w;
  c.height = h;
  return c;
};

const SignatureCanvas = forwardRef(function SignatureCanvas({ disabled = false, onStrokesChange, ariaLabel = "Lienzo de firma", className = "" }, ref) {
  const canvasRef = useRef(null);
  const strokesRef = useRef([]);
  const activeRef = useRef(null);
  const rafRef = useRef(0);
  const cssSizeRef = useRef({ width: 0, height: 0 });
  const onChangeRef = useRef(onStrokesChange);
  onChangeRef.current = onStrokesChange;

  const redraw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    let ctx = null;
    try {
      ctx = canvas.getContext("2d");
    } catch {
      ctx = null;
    }
    if (!ctx) return;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    const sx = canvas.width;
    const sy = canvas.height;
    const dpr = cssSizeRef.current.width ? sx / cssSizeRef.current.width : 1;
    drawStrokes(ctx, strokesRef.current, { map: (p) => ({ x: p.x * sx, y: p.y * sy }), lineWidth: PAD_LINE_WIDTH_CSS * dpr });
  }, []);

  const scheduleRedraw = useCallback(() => {
    if (rafRef.current) return;
    const raf = typeof window !== "undefined" && window.requestAnimationFrame ? window.requestAnimationFrame : (fn) => setTimeout(fn, 16);
    rafRef.current = raf(() => {
      rafRef.current = 0;
      redraw();
    });
  }, [redraw]);

  /** Resolución interna = tamaño CSS real × devicePixelRatio (se redibuja si cambió). */
  const syncSize = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const dpr = (typeof window !== "undefined" && window.devicePixelRatio) || 1;
    cssSizeRef.current = { width: rect.width, height: rect.height };
    const w = Math.max(1, Math.round(rect.width * dpr));
    const h = Math.max(1, Math.round(rect.height * dpr));
    if (canvas.width !== w || canvas.height !== h) {
      canvas.width = w;
      canvas.height = h;
    }
    redraw();
  }, [redraw]);

  useLayoutEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    syncSize();
    let observer = null;
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(() => syncSize());
      observer.observe(canvas);
    }
    // Cambio de devicePixelRatio (zoom del navegador, otro monitor): media query de la resolución actual.
    let mql = null;
    const onDprChange = () => {
      syncSize();
      listenDpr();
    };
    const listenDpr = () => {
      mql?.removeEventListener?.("change", onDprChange);
      if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
      mql = window.matchMedia(`(resolution: ${window.devicePixelRatio || 1}dppx)`);
      mql?.addEventListener?.("change", onDprChange);
    };
    listenDpr();
    window.addEventListener("resize", syncSize);
    return () => {
      observer?.disconnect();
      mql?.removeEventListener?.("change", onDprChange);
      window.removeEventListener("resize", syncSize);
      if (rafRef.current) {
        (window.cancelAnimationFrame || clearTimeout)(rafRef.current);
        rafRef.current = 0;
      }
    };
  }, [syncSize]);

  const notify = useCallback(() => onChangeRef.current?.(strokesRef.current.length), []);

  const pointOf = (event) => {
    const canvas = canvasRef.current;
    return canvas ? toNormalizedPoint(event.clientX, event.clientY, canvas.getBoundingClientRect()) : null;
  };

  const handlePointerDown = (event) => {
    if (disabled || activeRef.current) return;
    if (event.pointerType === "mouse" && event.button !== 0) return;
    event.preventDefault();
    syncSize();
    const canvas = canvasRef.current;
    try {
      canvas.setPointerCapture?.(event.pointerId);
    } catch {
      // sin captura el trazo igual se dibuja mientras el puntero esté encima
    }
    const point = pointOf(event);
    if (!point) return;
    const stroke = [point];
    strokesRef.current.push(stroke);
    activeRef.current = { pointerId: event.pointerId, stroke };
    scheduleRedraw();
    notify();
  };

  const handlePointerMove = (event) => {
    const active = activeRef.current;
    if (!active || active.pointerId !== event.pointerId) return;
    event.preventDefault();
    const native = event.nativeEvent;
    const samples = typeof native?.getCoalescedEvents === "function" ? native.getCoalescedEvents() : [];
    const list = samples && samples.length ? samples : [event];
    const rect = canvasRef.current.getBoundingClientRect();
    let added = false;
    for (const sample of list) {
      added = pushPoint(active.stroke, toNormalizedPoint(sample.clientX, sample.clientY, rect), { width: rect.width, height: rect.height }) || added;
    }
    if (added) scheduleRedraw();
  };

  const endStroke = (event) => {
    const active = activeRef.current;
    if (!active || active.pointerId !== event.pointerId) return;
    // El punto de suelta también cuenta (un toque corto sin movimiento queda como punto).
    if (event.type === "pointerup") {
      const rect = canvasRef.current.getBoundingClientRect();
      pushPoint(active.stroke, toNormalizedPoint(event.clientX, event.clientY, rect), { width: rect.width, height: rect.height });
    }
    activeRef.current = null;
    try {
      canvasRef.current?.releasePointerCapture?.(event.pointerId);
    } catch {
      // ya liberada
    }
    scheduleRedraw();
    notify();
  };

  useEffect(() => {
    if (disabled) activeRef.current = null;
  }, [disabled]);

  useImperativeHandle(
    ref,
    () => ({
      clear() {
        strokesRef.current = [];
        activeRef.current = null;
        redraw();
        notify();
      },
      isEmpty() {
        return strokesAreEmpty(strokesRef.current);
      },
      getStrokes() {
        return strokesRef.current.map((s) => s.map((p) => ({ ...p })));
      },
      /** PNG final de la firma para una casilla ({ x0, y0, x1, y1 } en puntos) o null si está vacío. */
      toSignaturePng({ inkBoxPt = null, createCanvas = createDomCanvas } = {}) {
        const { width, height } = cssSizeRef.current;
        if (strokesAreEmpty(strokesRef.current) || !width || !height) return null;
        return exportSignaturePng(strokesRef.current, { cssWidth: width, cssHeight: height, inkBoxPt, createCanvas });
      },
      getCanvas() {
        return canvasRef.current;
      },
    }),
    [notify, redraw]
  );

  return (
    <canvas
      ref={canvasRef}
      role="img"
      aria-label={ariaLabel}
      data-fedup-signature-canvas="true"
      className={["block h-full w-full select-none", disabled ? "cursor-not-allowed" : "cursor-crosshair", className].join(" ")}
      style={{ touchAction: "none" }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={endStroke}
      onPointerCancel={endStroke}
      onLostPointerCapture={endStroke}
      onContextMenu={(event) => event.preventDefault()}
    />
  );
});

export default SignatureCanvas;
